import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Check, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";
import { BrandLogo } from "@/components/BrandLogo";

const SALES_EMAIL = "sales@bdvgai.com";

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const org = useOrg();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
    if (params.get("checkout") === "cancelled") toast("Checkout cancelled — no charge was made.");
    // eslint-disable-next-line
  }, []);

  const buy = async (plan) => {
    if (plan.contact_sales) { window.location.href = `mailto:${SALES_EMAIL}?subject=BDVG Enterprise enquiry`; return; }
    if (!user) { nav("/login?mode=register"); return; }
    if (org?.current && !org.can("billing.manage")) { toast.error("Ask a workspace admin to manage billing."); return; }
    if (org?.subscription?.subscription_active) {
      try { await org.openPortal(); } catch { toast.error("Billing portal unavailable"); }
      return;
    }
    if (!plan.stripe_available) { toast("This plan isn't available for online purchase yet — contact us to get set up."); return; }
    setBusy(plan.code);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        plan: plan.code.replace("diamond_", ""),
        billing_period: interval === "year" ? "annual" : "monthly",
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
    finally { setBusy(""); }
  };

  const currentPlanId = org?.current?.org?.plan_id;

  return (
    <div className="min-h-screen bg-black text-white bg-noise">
      <header className="sticky top-0 z-30 glass border-b border-white/8">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <button onClick={() => nav(user ? "/app/dashboard" : "/")} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="pricing-back">
            <ArrowLeft className="h-4 w-4" /> <BrandLogo size={26} textClass="text-base" />
          </button>
          {!user && (
            <button onClick={() => nav("/login")} className="rounded-full px-4 py-2 text-sm text-white/70 hover:text-white" data-testid="pricing-signin">Sign In</button>
          )}
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">Simple, scalable pricing</h1>
          <p className="mx-auto mt-4 max-w-xl text-white/55">Every plan starts with a free trial. Pick the tier that fits — upgrade or downgrade anytime.</p>
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-white/10 p-1 text-sm">
            <button onClick={() => setInterval("month")} className={`rounded-full px-4 py-2 ${interval === "month" ? "bg-gold text-black" : "text-white/60"}`} data-testid="pricing-toggle-monthly">Monthly</button>
            <button onClick={() => setInterval("year")} className={`rounded-full px-4 py-2 ${interval === "year" ? "bg-gold text-black" : "text-white/60"}`} data-testid="pricing-toggle-annual">Annual <span className="text-xs opacity-70">save ~17%</span></button>
          </div>
        </div>

        {!plans ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {plans.map((p) => {
              const isPro = p.code === "diamond_pro";
              const isCurrent = p.id === currentPlanId;
              const price = interval === "year" ? p.price_annual : p.price_monthly;
              return (
                <div key={p.id} className={`flex flex-col rounded-2xl border p-6 ${isPro ? "border-gold/50 bg-gradient-to-b from-gold/[0.07] to-transparent shadow-gold-sm" : "border-white/8 bg-charcoal"}`} data-testid={`pricing-plan-${p.code}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-gold" /><h3 className="font-heading text-base font-semibold">{p.name}</h3></div>
                    {isPro && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold text-black">Popular</span>}
                  </div>
                  <p className="mt-1 text-xs text-white/45">{p.description}</p>
                  <p className="mt-4 font-heading text-3xl font-bold">{p.contact_sales ? "Custom" : `£${price}`}<span className="text-sm font-normal text-white/40">{p.contact_sales ? "" : `/${interval === "year" ? "yr" : "mo"}`}</span></p>

                  <ul className="mt-5 flex-1 space-y-2 text-sm text-white/65">
                    <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p.limits.companies === -1 ? "Unlimited companies" : `${p.limits.companies} ${p.limits.companies === 1 ? "company" : "companies"}`}</li>
                    <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p.limits.users === -1 ? "Custom" : p.limits.users} users</li>
                    <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p.limits.ai_requests === -1 ? "Custom" : p.limits.ai_requests.toLocaleString()} AI requests/mo</li>
                    <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p.modules === -1 ? "All modules" : `${p.modules} modules`}</li>
                    {p.contact_sales && <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Custom integrations & priority onboarding</li>}
                  </ul>

                  <div className="mt-6">
                    {isCurrent ? (
                      <div className="flex items-center justify-center gap-2 rounded-xl border border-gold/30 py-2.5 text-sm text-gold"><Check className="h-4 w-4" /> Current plan</div>
                    ) : p.contact_sales ? (
                      <button onClick={() => buy(p)} className="w-full rounded-xl border border-gold/30 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10" data-testid={`pricing-buy-${p.code}`}>Contact Sales</button>
                    ) : p.stripe_available || !user ? (
                      <button onClick={() => buy(p)} disabled={busy === p.code} className={`flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-transform hover:scale-[1.01] disabled:opacity-40 ${isPro ? "bg-gradient-to-r from-gold-light to-gold text-black" : "border border-gold/30 text-gold hover:bg-gold/10"}`} data-testid={`pricing-buy-${p.code}`}>
                        {busy === p.code ? <Loader2 className="h-4 w-4 animate-spin" /> : (user ? "Choose plan" : "Start free trial")}
                      </button>
                    ) : (
                      <div className="rounded-xl border border-white/10 py-2.5 text-center text-sm text-white/40" data-testid={`pricing-unavailable-${p.code}`}>Coming soon</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-10 text-center text-xs text-white/30">Payments are handled securely by Stripe — we never store your card details.</p>
      </section>
    </div>
  );
}
